import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import {
  Users, UserPlus, Search, SlidersHorizontal, ShieldCheck, ShieldOff,
  ExternalLink, Sparkles, Users2, Ban, RefreshCw, ChevronDown, UserCog, ShieldAlert, Archive,
} from 'lucide-react';
import Header from './Header';
import { formatDate } from '../utils/dateFormatter';
import InactiveAccountsWidget from './InactiveAccountsWidget';
import TwoSVEnforcementWidget from './TwoSVEnforcementWidget';
import PrivilegedAccountsWidget from './PrivilegedAccountsWidget';
import RecoveryContactsWidget from './RecoveryContactsWidget';
import SuspendedUsersWidget from './SuspendedUsersWidget';
import BulkActionBar from './BulkActionBar';
import IdentityActionsMenu from './IdentityActionsMenu';
import IdentityGroupsTable from './IdentityGroupsTable';
import PendingInvitesDialog from './PendingInvitesDialog';
import ControlDetailModalHost from './ControlDetailModalHost';
import { Checkbox } from './ui/checkbox';

const HUMAN_TYPE_LABELS = {
  employee: 'Employee',
  non_employee: 'Non-Employee',
  distribution_list: 'Distribution List',
};

const IDENTITY_TYPE_OPTIONS = Object.entries(HUMAN_TYPE_LABELS).map(([value, label]) => ({ value, label }));

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return (Date.now() - d.getTime()) / 86400000;
};

// ── Subtle badge helpers (no heavy black pills) ──────────────────────
const SoftBadge = ({ children, tone = 'slate', icon: Icon, title, testId }) => {
  const tones = {
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${tones[tone]}`}
      title={title}
      data-testid={testId}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
};

const roleTone = (role) => ({ owner: 'violet', admin: 'blue', employee: 'slate', auditor: 'amber' }[role] || 'slate');
const statusTone = (status) => ({ active: 'emerald', exited: 'rose', suspended: 'amber', non_employee: 'slate' }[status] || 'slate');

const PostureHintCard = ({ gwConnected }) => {
  const navigate = useNavigate();
  if (!gwConnected) return null;
  return (
    <Card className="border-dashed border-slate-200 bg-slate-50/60" data-testid="employees-posture-hint">
      <CardContent className="py-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-800">Looking for password policy, Drive sharing, Gmail security, DLP, or groups?</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Org-wide identity &amp; security signals live under
            <span className="font-medium text-slate-700"> Technology → Security Posture</span>.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate('/technologies/google-workspace?tab=posture')} data-testid="employees-go-to-posture-btn" className="shrink-0">
          Open Security Posture
          <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
};

const EmployeeList = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const workspaceId = currentWorkspace?.id || JSON.parse(localStorage.getItem('current_workspace') || 'null')?.id;
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default (primary) filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [twoSvFilter, setTwoSvFilter] = useState('all'); // all | enrolled | not_enrolled
  const [showArchived, setShowArchived] = useState(false);

  // Advanced filters (collapsed drawer)
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [identityTypeFilter, setIdentityTypeFilter] = useState([]); // server-side multi
  const [deptFilter, setDeptFilter] = useState('all');
  const [lastLoginFilter, setLastLoginFilter] = useState('all'); // all | never | inactive_30 | inactive_90
  const [onboardedFilter, setOnboardedFilter] = useState('all'); // all | last_30 | last_90 | this_year

  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [activeTab, setActiveTab] = useState('employees');
  const [groupCount, setGroupCount] = useState(null);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [pendingInviteCount, setPendingInviteCount] = useState(0);
  const [gwConnected, setGwConnected] = useState(false);
  const [m365Connected, setM365Connected] = useState(false);
  const [gwSyncing, setGwSyncing] = useState(false);
  const [gwSyncStats, setGwSyncStats] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchGwConnectionStatus();
    fetchGroupCount();
    refreshInviteCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when server-side filters change.
  useEffect(() => {
    fetchEmployees();
    setSelectedEmails(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityTypeFilter]);

  const fetchEmployees = async () => {
    try {
      const qs = new URLSearchParams();
      qs.append('archived', 'all');
      (identityTypeFilter || []).forEach((t) => qs.append('identity_type', t));
      const response = await axios.get(`/api/employees?${qs.toString()}`);
      setEmployees(response.data || []);
    } catch (error) {
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGwConnectionStatus = async () => {
    try {
      if (!user?.id) return;
      const ws = JSON.parse(localStorage.getItem('current_workspace') || 'null');
      if (!ws?.id) return;
      const resp = await axios.get(`/api/technologies/connections/${user.id}/${ws.id}`);
      const conns = resp.data?.connections || [];
      const gw = conns.find((c) => c.technology === 'google_workspace');
      const m365 = conns.find((c) => c.technology === 'microsoft_365');
      setGwConnected(!!gw && gw.status === 'active');
      setM365Connected(!!m365 && m365.status === 'active');
    } catch {
      setGwConnected(false);
      setM365Connected(false);
    }
  };

  const handleSyncDirectory = async () => {
    try {
      setGwSyncing(true);
      const ws = JSON.parse(localStorage.getItem('current_workspace') || 'null');
      if (!user?.id || !ws?.id) { toast.error('No active workspace'); return; }
      const agg = { synced: 0, updated: 0, marked_removed: 0, total_in_payload: 0, synced_at: null };
      let collectedAt = null;
      const apply = (r) => {
        const s = r?.data?.stats || {};
        agg.synced += s.synced || 0;
        agg.updated += s.updated || 0;
        agg.marked_removed += s.marked_removed || 0;
        agg.total_in_payload += s.total_in_payload || 0;
        agg.synced_at = s.synced_at || agg.synced_at;
        collectedAt = r?.data?.evidence_collected_at || collectedAt;
      };
      if (gwConnected) {
        apply(await axios.post(`/api/technologies/google-workspace/sync-employees`, { user_id: user.id, workspace_id: ws.id }));
      }
      if (m365Connected) {
        apply(await axios.post(`/api/technologies/microsoft-365/sync-employees`, { user_id: user.id, workspace_id: ws.id }));
      }
      setGwSyncStats({ ...agg, evidence_collected_at: collectedAt });
      toast.success(`Synced from directory · ${agg.synced} new, ${agg.updated} updated, ${agg.marked_removed} removed`);
      await fetchEmployees();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Sync failed');
    } finally {
      setGwSyncing(false);
    }
  };

  const fetchGroupCount = async () => {
    try {
      const r = await axios.get('/api/identity-groups');
      setGroupCount(Array.isArray(r.data) ? r.data.length : 0);
    } catch { setGroupCount(0); }
  };

  const refreshInviteCount = async () => {
    try {
      const r = await axios.get('/api/identity-invites');
      const list = Array.isArray(r.data) ? r.data : [];
      setPendingInviteCount(list.filter((i) => i.status === 'pending_approval' || i.status === 'invited').length);
    } catch { /* 403 for non-admins is expected */ }
  };

  const handleMarkStatus = async (employeeId, newStatus) => {
    try {
      await axios.post(`/api/employees/${employeeId}/mark-status`, null, { params: { status: newStatus } });
      toast.success(`Employee marked as ${newStatus}`);
      fetchEmployees();
    } catch { toast.error('Failed to update employee status'); }
  };

  const handleResendWelcome = async (employeeId) => {
    try {
      const res = await axios.post(`/api/employees/${employeeId}/resend-welcome`);
      toast.success(`Welcome email sent to ${res.data?.email || 'employee'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send welcome email');
    }
  };

  const handleChangeRole = async (employeeId, role) => {
    try {
      await axios.put(`/api/employees/${employeeId}`, { role });
      toast.success(`Role changed to ${role}`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    try {
      if (confirmAction.kind === 'mark-exited') await handleMarkStatus(confirmAction.employee.id, 'exited');
      else if (confirmAction.kind === 'mark-active') await handleMarkStatus(confirmAction.employee.id, 'active');
      else if (confirmAction.kind === 'resend-welcome') await handleResendWelcome(confirmAction.employee.id);
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────
  const departments = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => { const d = e.department || e.org_unit_path; if (d && d !== '/') set.add(d); });
    return Array.from(set).sort();
  }, [employees]);

  // An "active employee" stays on the Employees tab. Exited employees move
  // to the Non-Employees tab (alongside non_employee / distribution_list).
  const isActiveEmployee = (e) => (e.identity_type || 'employee') === 'employee' && (e.status || 'active') !== 'exited';

  const cardStats = useMemo(() => {
    let emp = 0, non = 0, adminOwner = 0, no2sv = 0, total = 0;
    employees.forEach((e) => {
      if (e.is_archived) return; // archived live in their own tab
      total++;
      if (isActiveEmployee(e)) emp++; else non++;
      const role = (e.role || '').toLowerCase();
      if (role === 'owner' || role === 'admin') adminOwner++;
      // Count only known-not-enrolled; null = unknown (license-gated) is excluded.
      if (e.is_enrolled_in_2sv !== true && e.is_enrolled_in_2sv !== null) no2sv++;
    });
    return { total, emp, non, adminOwner, no2sv };
  }, [employees]);

  const filteredAll = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const year = new Date().getFullYear();
    return employees.filter((e) => {
      if (q && !((e.email || '').toLowerCase().includes(q) || (e.full_name || '').toLowerCase().includes(q))) return false;
      if (statusFilter === 'suspended') { if (!e.is_suspended) return false; }
      else if (statusFilter === 'active') { if (e.is_suspended || (e.status || 'active') !== 'active') return false; }
      else if (statusFilter !== 'all' && (e.status || 'active') !== statusFilter) return false;
      if (roleFilter !== 'all' && e.role !== roleFilter) return false;
      if (twoSvFilter === 'enrolled' && e.is_enrolled_in_2sv !== true) return false;
      if (twoSvFilter === 'not_enrolled' && (e.is_enrolled_in_2sv === true || e.is_enrolled_in_2sv === null)) return false;
      if (deptFilter !== 'all' && (e.department || e.org_unit_path) !== deptFilter) return false;
      if (lastLoginFilter !== 'all') {
        const d = daysSince(e.last_login_time);
        if (lastLoginFilter === 'never' && d !== null) return false;
        if (lastLoginFilter === 'inactive_30' && !(d === null || d > 30)) return false;
        if (lastLoginFilter === 'inactive_90' && !(d === null || d > 90)) return false;
      }
      if (onboardedFilter !== 'all') {
        const od = e.onboarding_date ? new Date(e.onboarding_date) : null;
        if (!od || isNaN(od.getTime())) return false;
        const d = daysSince(e.onboarding_date);
        if (onboardedFilter === 'last_30' && d > 30) return false;
        if (onboardedFilter === 'last_90' && d > 90) return false;
        if (onboardedFilter === 'this_year' && od.getFullYear() !== year) return false;
      }
      return true;
    });
  }, [employees, searchQuery, statusFilter, roleFilter, twoSvFilter, deptFilter, lastLoginFilter, onboardedFilter]);

  const nonArchived = useMemo(() => filteredAll.filter((e) => !e.is_archived), [filteredAll]);
  const employeeRows = useMemo(() => nonArchived.filter(isActiveEmployee), [nonArchived]);
  const nonEmployeeRows = useMemo(() => nonArchived.filter((e) => !isActiveEmployee(e)), [nonArchived]);
  const archivedRows = useMemo(() => filteredAll.filter((e) => e.is_archived), [filteredAll]);
  const archivedCount = useMemo(() => employees.filter((e) => e.is_archived).length, [employees]);

  const advancedActiveCount =
    (identityTypeFilter.length ? 1 : 0) + (deptFilter !== 'all' ? 1 : 0) +
    (lastLoginFilter !== 'all' ? 1 : 0) + (onboardedFilter !== 'all' ? 1 : 0);

  // ── Cell renderers ────────────────────────────────────────────────
  const renderNameCell = (e) => {
    const typeLabel = HUMAN_TYPE_LABELS[e.identity_type] || (e.identity_type || 'employee').replace('_', ' ');
    const dept = (() => { const d = e.department || e.org_unit_path; return d && d !== '/' ? d : ''; })();
    return (
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
          {e.full_name || 'No name'}
          {e.source && e.source !== 'ethira' && (
            <span title={e.source === 'ethira+google_workspace' ? 'Nova account + directory' : 'Synced from directory'}
              className="inline-flex items-center text-[10px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded px-1 py-0.5">Synced</span>
          )}
        </div>
        <div className="text-xs text-slate-500">{e.email}</div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          <SoftBadge tone="slate" title={e.identity_type_reason || ''} testId={`identity-type-badge-${e.id}`}>{typeLabel}</SoftBadge>
          {dept && <span className="text-[11px] text-slate-400" title={e.org_unit_path || ''}>{dept}</span>}
          {e.suggested_identity_type && (
            <SoftBadge tone="violet" icon={Sparkles} title="Suggested by heuristic — accept via the row menu" testId={`identity-suggestion-${e.id}`}>
              Suggest: {HUMAN_TYPE_LABELS[e.suggested_identity_type] || e.suggested_identity_type.replace('_', ' ')}
            </SoftBadge>
          )}
          {e.source === 'google_workspace_group' && (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid={`identity-members-${e.id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100">
                  <Users2 className="w-3 h-3" />{e.group_member_count || 0} member{(e.group_member_count || 0) === 1 ? '' : 's'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" data-testid={`identity-members-popover-${e.id}`}>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{e.group_type || 'Group'} · {e.email}</div>
                {Array.isArray(e.group_members) && e.group_members.length > 0 ? (
                  <ul className="space-y-1.5 max-h-60 overflow-y-auto">
                    {e.group_members.map((m, idx) => (
                      <li key={`${e.id}-m-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-800 truncate">{m.email}</span>
                        {m.role && <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-50 text-slate-600 border-slate-200">{m.role.toLowerCase()}</span>}
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-slate-500">No members synced.</div>}
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
    );
  };

  const renderTwoSv = (e) => (
    e.is_enrolled_in_2sv === true ? (
      <SoftBadge tone="emerald" icon={ShieldCheck} testId={`2sv-${e.id}`}>{e.is_enforced_in_2sv ? 'Enforced' : 'Enrolled'}</SoftBadge>
    ) : e.is_enrolled_in_2sv === null ? (
      <span className="text-sm text-slate-400" title="MFA data not available on this tenant (Entra free tier / permission missing)" data-testid={`2sv-${e.id}`}>—</span>
    ) : <SoftBadge tone="amber" icon={ShieldOff} testId={`2sv-${e.id}`}>Not enrolled</SoftBadge>
  );

  const renderLastLogin = (e) => {
    const d = daysSince(e.last_login_time);
    if (!e.last_login_time) {
      // Sign-in data unavailable (license-gated / not collected) → Unknown, not "Never".
      if (e.last_login_known === false) {
        return <span className="text-sm text-slate-400" title="Sign-in data not available on this tenant (Entra ID P1 required)" data-testid={`lastlogin-${e.id}`}>—</span>;
      }
      return <SoftBadge tone="amber" testId={`lastlogin-${e.id}`}>Never</SoftBadge>;
    }
    return (
      <span className={`text-sm ${d > 90 ? 'text-amber-700' : 'text-slate-500'}`} title={d > 90 ? 'No recent login (90+ days)' : ''}>
        {formatDate(e.last_login_time)}
      </span>
    );
  };

  const renderActions = (e, variant) => (
    <div className="flex justify-end gap-2">
      <Button variant="outline" size="sm" onClick={() => navigate(`/employees/${e.id}`)} data-testid={`employee-view-btn-${e.id}`}>
        View
      </Button>
      <IdentityActionsMenu
        employee={e}
        onEdit={() => navigate(`/employees/${e.id}`)}
        onChangeRole={variant === 'employees' ? (role) => handleChangeRole(e.id, role) : undefined}
        onResendWelcome={variant === 'employees' ? () => setConfirmAction({ kind: 'resend-welcome', employee: e }) : undefined}
        onMarkStatus={variant === 'employees' ? (status) => setConfirmAction({ kind: status === 'exited' ? 'mark-exited' : 'mark-active', employee: e }) : undefined}
        onAfterMutate={() => { fetchEmployees(); refreshInviteCount(); }}
      />
    </div>
  );

  // Generic people table for both Employees & Non-Employees tabs
  const renderPeopleTable = (rows, variant) => {
    if (rows.length === 0) {
      return (
        <Card>
          <CardContent className="py-14 text-center">
            <Users className="w-14 h-14 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">
              {variant === 'employees' ? 'No employees found' : variant === 'archived' ? 'No archived identities' : 'No non-employee identities found'}
            </h3>
            <p className="text-slate-500 mb-6 text-sm">
              {variant === 'archived'
                ? 'Identities you archive will appear here. They stay searchable for audit history and can be restored anytime.'
                : (employees.length === 0
                  ? (variant === 'employees' ? 'Get started by adding your first employee' : 'Non-employee identities and shared mailboxes will appear here.')
                  : 'Try adjusting your search or filters.')}
            </p>
            {variant === 'employees' && employees.length === 0 && (
              <Button onClick={() => navigate('/employees/new')} className="bg-navy-600 hover:bg-navy-700 text-white">
                <UserPlus className="w-4 h-4 mr-2" /> Add your first employee
              </Button>
            )}
          </CardContent>
        </Card>
      );
    }
    const headers = ['Name', 'Role', 'Status', '2SV', 'Last Login', 'Onboarded'];
    const allSelected = rows.length > 0 && rows.every((e) => e.email && selectedEmails.has(e.email.toLowerCase()));
    return (
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <Checkbox data-testid={`select-all-${variant}`} aria-label="Select all" checked={allSelected}
                      onCheckedChange={(v) => {
                        const next = new Set(selectedEmails);
                        rows.forEach((e) => { if (e.email) { if (v) next.add(e.email.toLowerCase()); else next.delete(e.email.toLowerCase()); } });
                        setSelectedEmails(next);
                      }} />
                  </th>
                  {headers.map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {rows.map((e) => {
                  const emailKey = (e.email || '').toLowerCase();
                  const isChecked = emailKey && selectedEmails.has(emailKey);
                  const rowDim = e.is_archived;
                  return (
                    <tr key={e.id} data-testid={`employee-row-${e.id}`}
                      className={`transition-colors ${isChecked ? 'bg-emerald-50/40' : 'hover:bg-slate-50'} ${rowDim ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-4">
                        <Checkbox aria-label={`Select ${e.email || 'identity'}`} disabled={!emailKey} checked={!!isChecked}
                          onCheckedChange={(v) => setSelectedEmails((prev) => { const n = new Set(prev); if (v) n.add(emailKey); else n.delete(emailKey); return n; })}
                          data-testid={`select-employee-${e.id}`} />
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{renderNameCell(e)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {e.role ? <SoftBadge tone={roleTone(e.role)} testId={`role-${e.id}`}>{e.role}</SoftBadge> : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {e.is_suspended
                          ? <SoftBadge tone="amber" icon={Ban} title={e.suspend_reason || 'Login disabled by an administrator'} testId={`status-${e.id}`}>Suspended</SoftBadge>
                          : <SoftBadge tone={statusTone(e.status)} testId={`status-${e.id}`}>{(e.status || 'active').replace('_', ' ')}</SoftBadge>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{renderTwoSv(e)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{renderLastLogin(e)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-sm text-slate-500">
                        {e.onboarding_date ? formatDate(e.onboarding_date) : (e.created_at ? formatDate(e.created_at) : <span className="text-slate-400">—</span>)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right text-sm font-medium">{renderActions(e, variant)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Horizontal group of selectable filter pills (matches Audit Detail style)
  const empPillCls = (active) => `text-xs px-3 py-1 rounded-full border transition-colors ${
    active ? 'bg-[#0F62FE] text-white border-[#0F62FE]' : 'bg-white text-[#475569] border-[#D7E0F2] hover:bg-[#F1F5F9]'
  }`;
  const renderEmpPills = (options, value, onChange, testid) => (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          onClick={() => onChange(opt.value)}
          data-testid={`${testid}-${opt.value}`}
          className={empPillCls(value === opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // ── Reusable filter bar (shown on both people tabs) ───────────────
  const renderFilterBar = () => (
    <Card className="mb-6">
      <CardContent className="pt-6 space-y-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input type="text" placeholder="Search by name or email…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="employee-search-input" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(gwConnected || m365Connected) && (
              <Button onClick={handleSyncDirectory} disabled={gwSyncing} variant="outline" className="border-slate-300" data-testid="sync-from-gw-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${gwSyncing ? 'animate-spin' : ''}`} />
                {gwSyncing ? 'Syncing…' : 'Sync directory'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setInvitesOpen(true)} className="border-slate-300 relative" data-testid="open-pending-invites-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Employee invites
              {pendingInviteCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="pending-invites-badge">
                  {pendingInviteCount}
                </span>
              )}
            </Button>
            <Button onClick={() => navigate('/employees/new')} className="bg-navy-600 hover:bg-navy-700 text-white" data-testid="add-employee-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Employee
            </Button>
          </div>
        </div>

        {/* All filters as labeled pill rows (no accordion) */}
        <div className="space-y-2.5 pt-3 border-t border-[#F1F5F9]">
          <div className="flex items-start gap-3">
            <span className="text-[11px] font-semibold text-[#94A3B8] w-32 shrink-0 pt-1 uppercase tracking-wide">Status</span>
            {renderEmpPills([
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'exited', label: 'Exited' },
            ], statusFilter, setStatusFilter, 'filter-status')}
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[11px] font-semibold text-[#94A3B8] w-32 shrink-0 pt-1 uppercase tracking-wide">Role</span>
            {renderEmpPills([
              { value: 'all', label: 'All' },
              { value: 'owner', label: 'Owner' },
              { value: 'admin', label: 'Admin' },
              { value: 'employee', label: 'Employee' },
            ], roleFilter, setRoleFilter, 'filter-role')}
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[11px] font-semibold text-[#94A3B8] w-32 shrink-0 pt-1 uppercase tracking-wide">2SV</span>
            {renderEmpPills([
              { value: 'all', label: 'All' },
              { value: 'enrolled', label: 'Enrolled' },
              { value: 'not_enrolled', label: 'Not enrolled' },
            ], twoSvFilter, setTwoSvFilter, 'filter-2sv')}
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[11px] font-semibold text-[#94A3B8] w-32 shrink-0 pt-1 uppercase tracking-wide">Department / OU</span>
            {renderEmpPills([
              { value: 'all', label: 'All' },
              ...departments.map((d) => ({ value: d, label: d })),
            ], deptFilter, setDeptFilter, 'filter-department')}
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[11px] font-semibold text-[#94A3B8] w-32 shrink-0 pt-1 uppercase tracking-wide">Last login</span>
            {renderEmpPills([
              { value: 'all', label: 'All' },
              { value: 'never', label: 'Never logged in' },
              { value: 'inactive_30', label: 'Inactive 30+ days' },
              { value: 'inactive_90', label: 'Inactive 90+ days' },
            ], lastLoginFilter, setLastLoginFilter, 'filter-last-login')}
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[11px] font-semibold text-[#94A3B8] w-32 shrink-0 pt-1 uppercase tracking-wide">Onboarded</span>
            {renderEmpPills([
              { value: 'all', label: 'All' },
              { value: 'last_30', label: 'Last 30 days' },
              { value: 'last_90', label: 'Last 90 days' },
              { value: 'this_year', label: 'This year' },
            ], onboardedFilter, setOnboardedFilter, 'filter-onboarded')}
          </div>

          {(advancedActiveCount > 0 || statusFilter !== 'all' || roleFilter !== 'all' || twoSvFilter !== 'all') && (
            <div className="flex items-start gap-3 pt-1">
              <span className="w-32 shrink-0" />
              <button onClick={() => { setStatusFilter('all'); setRoleFilter('all'); setTwoSvFilter('all'); setIdentityTypeFilter([]); setDeptFilter('all'); setLastLoginFilter('all'); setOnboardedFilter('all'); }}
                className="text-xs text-slate-500 hover:text-slate-700 underline pt-0.5" data-testid="filters-clear-all">
                Clear all filters
              </button>
            </div>
          )}

          {(gwConnected || m365Connected) && gwSyncStats?.synced_at && (
            <div className="pt-1 text-xs text-slate-500">
              Last synced · <span className="font-medium text-slate-700">{formatDate(gwSyncStats.synced_at)}</span>
              {' · '}{gwSyncStats.total_in_payload || 0} users in directory
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600" />
        </div>
      </div>
    );
  }

  const summaryCards = [
    { key: 'total', label: 'Total Identities', value: cardStats.total, tone: 'text-slate-900', icon: Users, onClick: null },
    { key: 'emp', label: 'Employees', value: cardStats.emp, tone: 'text-navy-600', icon: Users, onClick: () => setActiveTab('employees') },
    { key: 'non', label: 'Non-Employees', value: cardStats.non, tone: 'text-slate-700', icon: UserCog, onClick: () => setActiveTab('non_employees') },
    { key: 'adm', label: 'Admins & Owners', value: cardStats.adminOwner, tone: 'text-blue-600', icon: ShieldCheck, onClick: null },
    { key: '2sv', label: '2SV Not Enrolled', value: cardStats.no2sv, tone: cardStats.no2sv > 0 ? 'text-amber-600' : 'text-slate-900', icon: ShieldOff, onClick: () => { setTwoSvFilter('not_enrolled'); setActiveTab('employees'); } },
  ];

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      <ControlDetailModalHost />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Employee Management</h1>
          <p className="text-slate-500 text-sm">Manage employees, non-employees and identity governance for audit readiness.</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {summaryCards.map((c) => (
            <button key={c.key} onClick={c.onClick || undefined} disabled={!c.onClick} data-testid={`summary-card-${c.key}`}
              className={`text-left rounded-xl border border-slate-200 bg-white p-4 transition-all ${c.onClick ? 'hover:border-navy-300 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">{c.label}</span>
                <c.icon className="w-4 h-4 text-slate-300" />
              </div>
              <div className={`text-3xl font-bold mt-2 ${c.tone}`}>{c.value}</div>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="employees-tabs">
          <TabsList className="mb-6">
            <TabsTrigger value="employees" data-testid="tab-employees">
              <Users className="w-4 h-4 mr-1.5" /> Employees
              <span className="ml-1.5 text-[10px] text-slate-500">({cardStats.emp})</span>
            </TabsTrigger>
            <TabsTrigger value="non_employees" data-testid="tab-non-employees">
              <UserCog className="w-4 h-4 mr-1.5" /> Non-Employees
              <span className="ml-1.5 text-[10px] text-slate-500">({cardStats.non})</span>
            </TabsTrigger>
            <TabsTrigger value="groups" data-testid="tab-groups">
              <Users2 className="w-4 h-4 mr-1.5" /> Distribution List
              {groupCount !== null && <span className="ml-1.5 text-[10px] text-slate-500">({groupCount})</span>}
            </TabsTrigger>
            <TabsTrigger value="governance" data-testid="tab-governance">
              <ShieldAlert className="w-4 h-4 mr-1.5" /> Governance Insights
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">
              <Archive className="w-4 h-4 mr-1.5" /> Archived
              <span className="ml-1.5 text-[10px] text-slate-500">({archivedCount})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="employees">
            {renderFilterBar()}
            <div className="mb-3 -mt-2 text-sm text-slate-500" data-testid="record-count-employees">
              Showing <span className="font-semibold text-slate-700">{employeeRows.length}</span> of <span className="font-semibold text-slate-700">{employees.filter((e) => !e.is_archived && isActiveEmployee(e)).length}</span> records
            </div>
            <BulkActionBar selectedEmails={Array.from(selectedEmails)} onClear={() => setSelectedEmails(new Set())} onAfterMutate={() => fetchEmployees()} />
            {renderPeopleTable(employeeRows, 'employees')}
          </TabsContent>

          <TabsContent value="non_employees">
            {renderFilterBar()}
            <div className="mb-3 -mt-2 text-sm text-slate-500" data-testid="record-count-non-employees">
              Showing <span className="font-semibold text-slate-700">{nonEmployeeRows.length}</span> of <span className="font-semibold text-slate-700">{employees.filter((e) => !e.is_archived && !isActiveEmployee(e)).length}</span> records
            </div>
            <BulkActionBar selectedEmails={Array.from(selectedEmails)} onClear={() => setSelectedEmails(new Set())} onAfterMutate={() => fetchEmployees()} />
            {renderPeopleTable(nonEmployeeRows, 'non_employees')}
          </TabsContent>

          <TabsContent value="groups">
            <IdentityGroupsTable />
          </TabsContent>

          <TabsContent value="governance">
            <div className="space-y-4" data-testid="governance-insights-content">
              <p className="text-sm text-slate-500">
                Posture findings and identity-governance signals — inactive accounts, 2-step verification, privileged users, suspended deprovisioning, and recovery-contact coverage.
              </p>
              <InactiveAccountsWidget workspaceId={workspaceId} gwConnected={gwConnected} userId={user?.id} />
              <TwoSVEnforcementWidget workspaceId={workspaceId} gwConnected={gwConnected} />
              <PrivilegedAccountsWidget workspaceId={workspaceId} gwConnected={gwConnected} />
              <SuspendedUsersWidget workspaceId={workspaceId} gwConnected={gwConnected} />
              <RecoveryContactsWidget workspaceId={workspaceId} gwConnected={gwConnected} />
              <PostureHintCard gwConnected={gwConnected} />
            </div>
          </TabsContent>

          <TabsContent value="archived">
            {renderFilterBar()}
            <div className="mb-3 -mt-2 text-sm text-slate-500" data-testid="record-count-archived">
              Showing <span className="font-semibold text-slate-700">{archivedRows.length}</span> of <span className="font-semibold text-slate-700">{archivedCount}</span> archived identities
            </div>
            <BulkActionBar selectedEmails={Array.from(selectedEmails)} onClear={() => setSelectedEmails(new Set())} onAfterMutate={() => fetchEmployees()} />
            {renderPeopleTable(archivedRows, 'archived')}
          </TabsContent>
        </Tabs>

        <PendingInvitesDialog open={invitesOpen} onOpenChange={(o) => { setInvitesOpen(o); if (!o) refreshInviteCount(); }} workspaceId={workspaceId} onCountChange={setPendingInviteCount} />
      </div>

      {/* Action confirmation dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open && !actionLoading) setConfirmAction(null); }}>
        <AlertDialogContent className="border-0 p-0 overflow-hidden" data-testid="employee-action-confirm-dialog">
          {(() => {
            if (!confirmAction) return null;
            const { kind, employee } = confirmAction;
            const name = employee.full_name || employee.email;
            const variants = {
              'mark-exited': { accent: 'from-red-500 to-rose-600', title: 'Mark employee as exited?', description: <><span className="font-medium text-slate-900">{name}</span> will be marked as exited. Access is revoked immediately, but their record and compliance history stay preserved for audit purposes.</>, confirmLabel: 'Yes, mark as exited', confirmClass: 'bg-red-600 hover:bg-red-700 text-white' },
              'mark-active': { accent: 'from-emerald-500 to-green-600', title: 'Reactivate this employee?', description: <><span className="font-medium text-slate-900">{name}</span> will be reactivated and regain access based on their role.</>, confirmLabel: 'Yes, reactivate', confirmClass: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
              'resend-welcome': { accent: 'from-navy-600 to-blue-600', title: 'Resend welcome email?', description: <>A fresh welcome email will be sent to <span className="font-medium text-slate-900">{employee.email}</span>.</>, confirmLabel: 'Send welcome email', confirmClass: 'bg-navy-600 hover:bg-navy-700 text-white' },
            };
            const v = variants[kind];
            return (
              <>
                <div className={`bg-gradient-to-r ${v.accent} px-6 py-5`}>
                  <div className="text-xs font-semibold uppercase tracking-wider text-white/80">Nova</div>
                  <AlertDialogTitle className="text-white text-lg leading-tight">{v.title}</AlertDialogTitle>
                </div>
                <div className="px-6 py-5 space-y-4">
                  <AlertDialogHeader className="space-y-1">
                    <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">{v.description}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={actionLoading} data-testid="employee-action-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction disabled={actionLoading} onClick={(e) => { e.preventDefault(); handleConfirmAction(); }} className={v.confirmClass} data-testid="employee-action-confirm-btn">
                      {actionLoading ? 'Working…' : v.confirmLabel}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </div>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EmployeeList;
